import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { BACKSPACE, DELETE, SPACE } from '@angular/cdk/keycodes';
import { Component, ContentChild, Directive, EventEmitter, forwardRef, Input, Output, } from '@angular/core';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common";
function Chip_i_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "i", 1);
} }
const _c0 = ["*"];
/** Event object emitted by MatChip when selected or deselected. */
export class ChipSelectionChange {
    constructor(
    /** Reference to the chip that emitted the event. */
    source, 
    /** Whether the chip that emitted the event is selected. */
    selected, 
    /** Whether the selection change was a result of a user interaction. */
    isUserInput = false) {
        this.source = source;
        this.selected = selected;
        this.isUserInput = isUserInput;
    }
}
/**
 * Dummy directive to add CSS class to chip trailing icon.
 * @docs-private
 */
// @Directive({
//   selector: 'mat-chip-trailing-icon, [matChipTrailingIcon]',
//   host: {'class': 'mat-chip-trailing-icon'}
// })
// export class MatChipTrailingIcon {}
/**
 * Material design styled Chip component. Used inside the MatChipList component.
 */
export class Chip {
    constructor(_elementRef, _ngZone) {
        this._elementRef = _elementRef;
        this._ngZone = _ngZone;
        /** Whether the chip has focus. */
        this._hasFocus = false;
        /** Whether the chip list is selectable */
        this.chipListSelectable = true;
        this._selected = false;
        this._selectable = true;
        this._removable = true;
        /** Emits when the chip is focused. */
        this._onFocus = new Subject();
        /** Emits when the chip is blured. */
        this._onBlur = new Subject();
        /** Emitted when the chip is selected or deselected. */
        this.selectionChange = new EventEmitter();
        /** Emitted when the chip is destroyed. */
        this.destroyed = new EventEmitter();
        /** Emitted when a chip is to be removed. */
        this.removed = new EventEmitter();
        this.disabled = false;
    }
    /** Whether the chip is selected. */
    get selected() {
        return this._selected;
    }
    set selected(value) {
        const coercedValue = coerceBooleanProperty(value);
        if (coercedValue !== this._selected) {
            this._selected = coercedValue;
            this._dispatchSelectionChange();
        }
    }
    /** The value of the chip. Defaults to the content inside `<mat-chip>` tags. */
    get value() {
        return this._value != undefined ? this._value : this._elementRef.nativeElement.textContent;
    }
    set value(value) {
        this._value = value;
    }
    /**
     * Whether or not the chip is selectable. When a chip is not selectable,
     * changes to it's selected state are always ignored. By default a chip is
     * selectable, and it becomes non-selectable if it's parent chip list is
     * not selectable.
     */
    get selectable() {
        return this._selectable && this.chipListSelectable;
    }
    set selectable(value) {
        this._selectable = coerceBooleanProperty(value);
    }
    /**
     * Determines whether or not the chip displays the remove styling and emits (removed) events.
     */
    get removable() {
        return this._removable;
    }
    set removable(value) {
        this._removable = coerceBooleanProperty(value);
    }
    /** The ARIA selected applied to the chip. */
    get ariaSelected() {
        return this.selectable ? this.selected.toString() : null;
    }
    ngOnDestroy() {
        this.destroyed.emit({ chip: this });
    }
    /** Selects the chip. */
    select() {
        if (!this._selected) {
            this._selected = true;
            this._dispatchSelectionChange();
        }
    }
    /** Deselects the chip. */
    deselect() {
        if (this._selected) {
            this._selected = false;
            this._dispatchSelectionChange();
        }
    }
    /** Select this chip and emit selected event */
    selectViaInteraction() {
        if (!this._selected) {
            this._selected = true;
            this._dispatchSelectionChange(true);
        }
    }
    /** Toggles the current selected state of this chip. */
    toggleSelected(isUserInput = false) {
        this._selected = !this.selected;
        this._dispatchSelectionChange(isUserInput);
        return this.selected;
    }
    /** Allows for programmatic focusing of the chip. */
    focus() {
        if (!this._hasFocus) {
            this._hasFocus = true;
            this._elementRef.nativeElement.focus();
            this._onFocus.next({ chip: this });
        }
        this._hasFocus = true;
    }
    /**
     * Allows for programmatic removal of the chip. Called by the MatChipList when the DELETE or
     * BACKSPACE keys are pressed.
     *
     * Informs any listeners of the removal request. Does not remove the chip from the DOM.
     */
    remove() {
        if (this.removable) {
            this.removed.emit({ chip: this });
        }
    }
    /** Handles click events on the chip. */
    _handleClick(event) {
        if (this.disabled) {
            event.preventDefault();
            event.stopPropagation();
        }
        else {
            event.stopPropagation();
        }
    }
    /** Handle custom key presses. */
    _handleKeydown(event) {
        if (this.disabled) {
            return;
        }
        switch (event.keyCode) {
            case DELETE:
            case BACKSPACE:
                // If we are removable, remove the focused chip
                this.remove();
                // Always prevent so page navigation does not occur
                event.preventDefault();
                break;
            case SPACE:
                // If we are selectable, toggle the focused chip
                if (this.selectable) {
                    this.toggleSelected(true);
                }
                // Always prevent space from scrolling the page since the list has focus
                event.preventDefault();
                break;
        }
    }
    _blur() {
        // When animations are enabled, Angular may end up removing the chip from the DOM a little
        // earlier than usual, causing it to be blurred and throwing off the logic in the chip list
        // that moves focus not the next item. To work around the issue, we defer marking the chip
        // as not focused until the next time the zone stabilizes.
        this._ngZone.onStable
            .asObservable()
            .pipe(take(1))
            .subscribe(() => {
            this._ngZone.run(() => {
                this._hasFocus = false;
                this._onBlur.next({ chip: this });
            });
        });
    }
    _dispatchSelectionChange(isUserInput = false) {
        this.selectionChange.emit({
            source: this,
            isUserInput,
            selected: this._selected,
        });
    }
}
Chip.ɵfac = function Chip_Factory(t) { return new (t || Chip)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.NgZone)); };
Chip.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: Chip, selectors: [["mhe-chip"], ["", "mhe-chip", ""]], contentQueries: function Chip_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
        i0.ɵɵcontentQuery(dirIndex, ChipRemove, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.removeIcon = _t.first);
    } }, hostAttrs: ["role", "option", 1, "mhe-chip"], hostVars: 10, hostBindings: function Chip_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("click", function Chip_click_HostBindingHandler($event) { return ctx._handleClick($event); })("keydown", function Chip_keydown_HostBindingHandler($event) { return ctx._handleKeydown($event); })("focus", function Chip_focus_HostBindingHandler() { return ctx.focus(); })("blur", function Chip_blur_HostBindingHandler() { return ctx._blur(); });
    } if (rf & 2) {
        i0.ɵɵattribute("tabindex", ctx.disabled ? null : -1)("disabled", ctx.disabled || null)("aria-disabled", ctx.disabled.toString())("aria-selected", ctx.ariaSelected);
        i0.ɵɵclassProp("chip-selected", ctx.selected)("chip-with-trailing-icon", ctx.removeIcon)("chip-disabled", ctx.disabled);
    } }, inputs: { disabled: "disabled", selected: "selected", value: "value", selectable: "selectable", removable: "removable" }, outputs: { selectionChange: "selectionChange", destroyed: "destroyed", removed: "removed" }, exportAs: ["mheChip"], ngContentSelectors: _c0, decls: 3, vars: 1, consts: [["class", "dpg-icon", "chipRemove", "", 4, "ngIf"], ["chipRemove", "", 1, "dpg-icon"]], template: function Chip_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "span");
        i0.ɵɵprojection(1);
        i0.ɵɵtemplate(2, Chip_i_2_Template, 1, 0, "i", 0);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", ctx.removable);
    } }, dependencies: function () { return [i1.NgIf, ChipRemove]; }, styles: ["[_nghost-%COMP%]{display:inline-block;border:1px solid gray;border-radius:4px;color:gray;font-size:14px;margin:0 10px 10px 0;padding:0 8px;cursor:default}[_nghost-%COMP%]:focus{outline:none}.chip-list[_nghost-%COMP%]{border:none;padding:0;margin:0}.mhe-chip.chip-disabled[_nghost-%COMP%]{cursor:not-allowed}.dpg-icon.chip-remove[_ngcontent-%COMP%]{position:relative;top:2px;left:6px;height:16px;width:16px;padding-right:4px}\n"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(Chip, [{
        type: Component,
        args: [{ selector: 'mhe-chip, [mhe-chip]', inputs: ['disabled'], exportAs: 'mheChip', host: {
                    class: 'mhe-chip',
                    '[attr.tabindex]': 'disabled ? null : -1',
                    role: 'option',
                    '[class.chip-selected]': 'selected',
                    '[class.chip-with-trailing-icon]': 'removeIcon',
                    //  '[class.chip-with-trailing-icon]': 'trailingIcon || removeIcon',
                    '[class.chip-disabled]': 'disabled',
                    '[attr.disabled]': 'disabled || null',
                    '[attr.aria-disabled]': 'disabled.toString()',
                    '[attr.aria-selected]': 'ariaSelected',
                    '(click)': '_handleClick($event)',
                    '(keydown)': '_handleKeydown($event)',
                    '(focus)': 'focus()',
                    '(blur)': '_blur()',
                }, template: "<span><ng-content></ng-content><i class=\"dpg-icon\" *ngIf=\"removable\" chipRemove></i></span>\n", styles: [":host{display:inline-block;border:1px solid gray;border-radius:4px;color:gray;font-size:14px;margin:0 10px 10px 0;padding:0 8px;cursor:default}:host:focus{outline:none}:host.chip-list{border:none;padding:0;margin:0}:host.mhe-chip.chip-disabled{cursor:not-allowed}.dpg-icon.chip-remove{position:relative;top:2px;left:6px;height:16px;width:16px;padding-right:4px}/*# sourceMappingURL=chip.css.map */\n"] }]
    }], function () { return [{ type: i0.ElementRef }, { type: i0.NgZone }]; }, { removeIcon: [{
            type: ContentChild,
            args: [forwardRef(() => ChipRemove)]
        }], selected: [{
            type: Input
        }], value: [{
            type: Input
        }], selectable: [{
            type: Input
        }], removable: [{
            type: Input
        }], selectionChange: [{
            type: Output
        }], destroyed: [{
            type: Output
        }], removed: [{
            type: Output
        }] }); })();
/**
 * Applies proper (click) support and adds styling for use with the Material Design "cancel" icon
 * available at https://material.io/icons/#ic_cancel.
 *
 * Example:
 *
 *     `<mat-chip>
 *       <mat-icon matChipRemove>cancel</mat-icon>
 *     </mat-chip>`
 *
 * You *may* use a custom icon, but you may need to override the `mat-chip-remove` positioning
 * styles to properly center the icon within the chip.
 */
export class ChipRemove {
    constructor(_parentChip) {
        this._parentChip = _parentChip;
    }
    /** Calls the parent chip's public `remove()` method if applicable. */
    _handleClick(event) {
        if (this._parentChip.disabled) {
            event.stopPropagation();
        }
        else if (this._parentChip.removable) {
            this._parentChip.remove();
        }
        // We need to stop event propagation because otherwise the event will bubble up to the
        // form field and cause the `onContainerClick` method to be invoked. This method would then
        // reset the focused chip that has been focused after chip removal. Usually the parent
        // the parent click listener of the `MatChip` would prevent propagation, but it can happen
        // that the chip is being removed before the event bubbles up.
        event.stopPropagation();
    }
}
ChipRemove.ɵfac = function ChipRemove_Factory(t) { return new (t || ChipRemove)(i0.ɵɵdirectiveInject(Chip)); };
ChipRemove.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: ChipRemove, selectors: [["", "chipRemove", ""]], hostAttrs: [1, "chip-remove", "chip-trailing-icon"], hostBindings: function ChipRemove_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("click", function ChipRemove_click_HostBindingHandler($event) { return ctx._handleClick($event); });
    } } });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ChipRemove, [{
        type: Directive,
        args: [{
                selector: '[chipRemove]',
                host: {
                    class: 'chip-remove chip-trailing-icon',
                    '(click)': '_handleClick($event)',
                },
            }]
    }], function () { return [{ type: Chip }]; }, null); })();
